import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { ws } from '../lib/api';
import { topics } from '@sahaj/websocket';
import { useWebRTC } from '../hooks/useWebRTC';
import { ringtonePlayer } from '../utils/ringtone';
import type { CallSignalMessage, CallType } from '@sahaj/types';

export type CallState =
  | 'IDLE'
  | 'OUTGOING_RINGING'
  | 'INCOMING_RINGING'
  | 'CONNECTING'
  | 'CONNECTED'
  | 'ENDED';

export interface ActiveCallMeta {
  callId: string;
  workspaceId: string;
  conversationId?: string | undefined;
  channelId?: string | undefined;
  callerId: string;
  callerName?: string | undefined;
  callerAvatar?: string | undefined;
  recipientId: string;
  recipientName?: string | undefined;
  recipientAvatar?: string | undefined;
  callType: CallType;
}

interface CallContextType {
  callState: CallState;
  activeCall: ActiveCallMeta | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  screenStream: MediaStream | null;
  isMuted: boolean;
  isCameraOff: boolean;
  isScreenSharing: boolean;
  audioLevel: number;
  durationSeconds: number;
  initiateCall: (params: {
    workspaceId: string;
    conversationId?: string | undefined;
    channelId?: string | undefined;
    recipientId: string;
    recipientName: string;
    recipientAvatar?: string | undefined;
    callType: CallType;
  }) => Promise<void>;
  acceptCall: () => Promise<void>;
  rejectCall: () => void;
  endCall: () => void;
  toggleMute: () => void;
  toggleCamera: () => void;
  toggleScreenShare: () => void;
}

const CallContext = createContext<CallContextType | undefined>(undefined);

export const CallProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [callState, setCallState] = useState<CallState>('IDLE');
  const [activeCall, setActiveCall] = useState<ActiveCallMeta | null>(null);
  const [durationSeconds, setDurationSeconds] = useState(0);

  const userRef = useRef(user);
  userRef.current = user;

  const activeCallRef = useRef<ActiveCallMeta | null>(null);
  activeCallRef.current = activeCall;

  const callStateRef = useRef<CallState>('IDLE');
  callStateRef.current = callState;

  const durationTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Track whether THIS tab/session accepted the call (to filter ANSWERED_ELSEWHERE CALL_CANCEL)
  const iAcceptedCallRef = useRef(false);
  // Track whether THIS peer is the caller (initiates WebRTC offer)
  const isCallerRef = useRef(false);

  // Helper: get the other peer's userId
  const getPeerUserId = useCallback((call: ActiveCallMeta, currentUserId: string): string => {
    return call.callerId === currentUserId ? call.recipientId : call.callerId;
  }, []);

  // Outbound signal dispatch via STOMP /app/call.signal
  const handleSignalOut = useCallback((signal: Partial<CallSignalMessage>) => {
    if (!activeCallRef.current || !userRef.current) return;
    const peerId = getPeerUserId(activeCallRef.current, userRef.current.id);

    console.log(`[CALL:${activeCallRef.current.callId}][${isCallerRef.current ? 'CALLER' : 'CALLEE'}] Sending signal:`, signal.type, '-> peer:', peerId);

    ws.send('/app/call.signal', {
      callId: activeCallRef.current.callId,
      workspaceId: activeCallRef.current.workspaceId,
      conversationId: activeCallRef.current.conversationId,
      channelId: activeCallRef.current.channelId,
      recipientId: peerId,
      callType: activeCallRef.current.callType,
      type: signal.type ?? 'CALL_OFFER',
      payload: signal.payload,
    });
  }, [getPeerUserId]);

  const {
    localStream,
    remoteStream,
    screenStream,
    isMuted,
    isCameraOff,
    isScreenSharing,
    audioLevel,
    acquireMedia,
    startConnection,
    handleRemoteSignal,
    toggleMute,
    toggleCamera,
    toggleScreenShare,
    cleanup: cleanupWebRTC,
  } = useWebRTC({
    onSignal: handleSignalOut,
    onConnectionStateChange: (state) => {
      const callId = activeCallRef.current?.callId ?? '?';
      const role = isCallerRef.current ? 'CALLER' : 'CALLEE';
      console.log(`[CALL:${callId}][${role}] RTCPeerConnection connectionState:`, state);

      if (state === 'connected') {
        setCallState('CONNECTED');
        ringtonePlayer.stop();
      } else if (state === 'disconnected') {
        // Transient - do NOT hang up yet. Give ICE a chance to recover.
        console.warn(`[CALL:${callId}][${role}] Connection disconnected - waiting for ICE recovery...`);
      } else if (state === 'failed') {
        // ICE definitively failed - terminate
        console.error(`[CALL:${callId}][${role}] Connection FAILED - terminating call`);
        endCallRef.current();
      }
      // 'closed' is NOT handled here — we only get 'closed' after WE called pc.close()
      // which means cleanup already ran. Triggering endCall() from 'closed' causes a
      // CALL_HANGUP loop to the remote peer who already tore down.
    },
  });

  const acquireMediaRef = useRef(acquireMedia);
  acquireMediaRef.current = acquireMedia;

  const startConnectionRef = useRef(startConnection);
  startConnectionRef.current = startConnection;

  const handleRemoteSignalRef = useRef(handleRemoteSignal);
  handleRemoteSignalRef.current = handleRemoteSignal;

  const cleanupWebRTCRef = useRef(cleanupWebRTC);
  cleanupWebRTCRef.current = cleanupWebRTC;

  // endCall ref so onConnectionStateChange closure always calls latest version
  const endCallRef = useRef<() => void>(() => {});

  const stopCallDuration = useCallback(() => {
    if (durationTimerRef.current) {
      clearInterval(durationTimerRef.current);
      durationTimerRef.current = null;
    }
    setDurationSeconds(0);
  }, []);

  const startCallDuration = useCallback(() => {
    stopCallDuration();
    setDurationSeconds(0);
    durationTimerRef.current = setInterval(() => {
      setDurationSeconds((prev) => prev + 1);
    }, 1000);
  }, [stopCallDuration]);

  // Terminate call locally and cleanly
  const endCall = useCallback(() => {
    const currentCall = activeCallRef.current;
    const currentUser = userRef.current;

    console.log(`[CALL:${currentCall?.callId ?? '?'}] endCall() called — callState was:`, callStateRef.current);

    ringtonePlayer.stop();
    ringtonePlayer.playEndBeep();
    stopCallDuration();

    // Only send HANGUP if we have an active call (not already IDLE)
    if (currentCall && currentUser && callStateRef.current !== 'IDLE') {
      const peerId = getPeerUserId(currentCall, currentUser.id);
      ws.send('/app/call.hangup', {
        callId: currentCall.callId,
        workspaceId: currentCall.workspaceId,
        conversationId: currentCall.conversationId,
        channelId: currentCall.channelId,
        recipientId: peerId,
        callType: currentCall.callType,
        type: 'CALL_HANGUP',
      });
    }

    iAcceptedCallRef.current = false;
    isCallerRef.current = false;
    cleanupWebRTCRef.current();
    setActiveCall(null);
    setCallState('IDLE');
  }, [getPeerUserId, stopCallDuration]);

  // Keep ref in sync
  endCallRef.current = endCall;

  // Outgoing Call Initiation
  const initiateCall = useCallback(
    async (params: {
      workspaceId: string;
      conversationId?: string | undefined;
      channelId?: string | undefined;
      recipientId: string;
      recipientName: string;
      recipientAvatar?: string | undefined;
      callType: CallType;
    }) => {
      if (!user) return;
      cleanupWebRTCRef.current();
      iAcceptedCallRef.current = false;
      isCallerRef.current = true; // This peer is the CALLER

      setActiveCall({
        callId: '', // Populated by server CALL_RINGING response
        workspaceId: params.workspaceId,
        conversationId: params.conversationId,
        channelId: params.channelId,
        callerId: user.id,
        callerName: user.displayName,
        callerAvatar: user.avatarUrl,
        recipientId: params.recipientId,
        recipientName: params.recipientName,
        recipientAvatar: params.recipientAvatar,
        callType: params.callType,
      });

      setCallState('OUTGOING_RINGING');
      ringtonePlayer.playOutgoingChime();

      // Immediately acquire camera/mic preview so the caller's video/audio is active right away
      acquireMediaRef.current(params.callType).catch((err) => {
        console.warn('[CallContext] Preview media access warning:', err);
      });

      ws.send('/app/call.initiate', {
        workspaceId: params.workspaceId,
        conversationId: params.conversationId,
        channelId: params.channelId,
        recipientId: params.recipientId,
        callType: params.callType,
      });
    },
    [user]
  );

  // Accept Incoming Call (CALLEE only)
  const acceptCall = useCallback(async () => {
    if (!activeCallRef.current || !userRef.current) return;
    ringtonePlayer.stop();
    setCallState('CONNECTING');
    startCallDuration();
    iAcceptedCallRef.current = true; // Mark that THIS tab accepted
    isCallerRef.current = false; // This peer is the CALLEE

    const currentCall = activeCallRef.current;
    const peerId = getPeerUserId(currentCall, userRef.current.id);

    console.log(`[CALL:${currentCall.callId}][CALLEE] acceptCall() — acquiring media, sending CALL_ACCEPT`);

    try {
      // CALLEE: start connection (acquire media + create RTCPeerConnection)
      // BUT do NOT send offer — wait for CALLER's offer instead.
      // The onnegotiationneeded in useWebRTC fires when tracks are added.
      // We suppress initial offer from callee by letting Perfect Negotiation handle it.
      await startConnectionRef.current(
        currentCall.callType,
        currentCall.callerId,
        currentCall.recipientId,
        currentCall.callId,
        false  // CALLEE — does not initiate offer
      );

      // Send CALL_ACCEPT AFTER media is ready so caller can trust we're ready
      ws.send('/app/call.accept', {
        callId: currentCall.callId,
        workspaceId: currentCall.workspaceId,
        conversationId: currentCall.conversationId,
        channelId: currentCall.channelId,
        recipientId: peerId,
        type: 'CALL_ACCEPT',
        callType: currentCall.callType,
      });
    } catch (err) {
      console.error('[CALL] Failed to accept call:', err);
      iAcceptedCallRef.current = false;
      endCallRef.current();
    }
  }, [getPeerUserId, startCallDuration]);

  // Reject Incoming Call
  const rejectCall = useCallback(() => {
    ringtonePlayer.stop();
    if (activeCallRef.current && userRef.current) {
      const peerId = getPeerUserId(activeCallRef.current, userRef.current.id);
      ws.send('/app/call.reject', {
        callId: activeCallRef.current.callId,
        workspaceId: activeCallRef.current.workspaceId,
        conversationId: activeCallRef.current.conversationId,
        channelId: activeCallRef.current.channelId,
        recipientId: peerId,
        type: 'CALL_REJECT',
        callType: activeCallRef.current.callType,
      });
    }
    iAcceptedCallRef.current = false;
    isCallerRef.current = false;
    setActiveCall(null);
    setCallState('IDLE');
  }, [getPeerUserId]);

  // Stable STOMP subscription on /user/queue/calls
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = ws.subscribe(topics.calls(), async (signal: CallSignalMessage) => {
      const callId = signal.callId ?? '?';
      console.log(`[CALL:${callId}] STOMP RX:`, signal.type, 'payload:', signal.payload);

      if (signal.type === 'CALL_RINGING') {
        const isIncoming = signal.payload?.direction === 'INCOMING';
        const callerName = signal.payload?.callerName || (isIncoming ? 'Caller' : userRef.current?.displayName);
        const callerAvatar = signal.payload?.callerAvatar || (isIncoming ? undefined : userRef.current?.avatarUrl);
        const recipientName = signal.payload?.recipientName || (isIncoming ? userRef.current?.displayName : undefined);
        const recipientAvatar = signal.payload?.recipientAvatar || (isIncoming ? userRef.current?.avatarUrl : undefined);

        setActiveCall((prev) => ({
          callId: signal.callId,
          workspaceId: signal.workspaceId,
          conversationId: signal.conversationId,
          channelId: signal.channelId,
          // canonical: senderId is ALWAYS the caller, recipientId is ALWAYS the callee
          callerId: signal.senderId,
          callerName: callerName || prev?.callerName,
          callerAvatar: callerAvatar || prev?.callerAvatar,
          recipientId: signal.recipientId,
          recipientName: recipientName || prev?.recipientName,
          recipientAvatar: recipientAvatar || prev?.recipientAvatar,
          callType: signal.callType,
        }));

        if (isIncoming) {
          isCallerRef.current = false;
          setCallState('INCOMING_RINGING');
          ringtonePlayer.playIncomingRing();
        } else {
          // Outgoing confirmation - callId is now assigned by server; update it
          setActiveCall((prev) =>
            prev ? { ...prev, callId: signal.callId } : null
          );
          isCallerRef.current = true;
          setCallState('OUTGOING_RINGING');
        }
      } else if (signal.type === 'CALL_ACCEPT') {
        // CALLER receives CALL_ACCEPT from Callee
        // Now CALLER initializes WebRTC and sends the initial offer
        console.log(`[CALL:${callId}][CALLER] Received CALL_ACCEPT — starting WebRTC + sending offer`);
        ringtonePlayer.stop();
        setCallState('CONNECTING');
        startCallDuration();

        if (activeCallRef.current) {
          // Update callId and metadata in activeCall
          const callerName = signal.payload?.callerName;
          const callerAvatar = signal.payload?.callerAvatar;
          const recipientName = signal.payload?.recipientName;
          const recipientAvatar = signal.payload?.recipientAvatar;

          setActiveCall((prev) =>
            prev
              ? {
                  ...prev,
                  callId: signal.callId,
                  callerName: callerName || prev.callerName,
                  callerAvatar: callerAvatar || prev.callerAvatar,
                  recipientName: recipientName || prev.recipientName,
                  recipientAvatar: recipientAvatar || prev.recipientAvatar,
                }
              : null
          );

          await startConnectionRef.current(
            activeCallRef.current.callType,
            activeCallRef.current.callerId,
            activeCallRef.current.recipientId,
            signal.callId,
            true  // CALLER — initiates offer via onnegotiationneeded
          );
          // onnegotiationneeded fires automatically when tracks are added,
          // sending CALL_OFFER to callee. No need to manually trigger it here.
        }
      } else if (signal.type === 'CALL_OFFER' || signal.type === 'CALL_ANSWER' || signal.type === 'ICE_CANDIDATE') {
        console.log(`[CALL:${callId}][${isCallerRef.current ? 'CALLER' : 'CALLEE'}] SDP/ICE signal:`, signal.type);
        if (activeCallRef.current) {
          await handleRemoteSignalRef.current(
            signal,
            activeCallRef.current.callerId,
            activeCallRef.current.recipientId
          );
        }
      } else if (signal.type === 'CALL_CANCEL') {
        // CRITICAL FIX: The server sends CALL_CANCEL to callee after they accept
        // with reason=ANSWERED_ELSEWHERE to dismiss other tabs.
        // If THIS tab is the one that accepted, ignore this CALL_CANCEL.
        const reason = signal.payload?.reason as string | undefined;
        if (reason === 'ANSWERED_ELSEWHERE' && iAcceptedCallRef.current) {
          console.log(`[CALL:${callId}] Ignoring ANSWERED_ELSEWHERE CALL_CANCEL — this tab accepted`);
          return;
        }

        console.log(`[CALL:${callId}] CALL_CANCEL received, reason:`, reason);
        ringtonePlayer.stop();
        ringtonePlayer.playEndBeep();
        stopCallDuration();
        iAcceptedCallRef.current = false;
        isCallerRef.current = false;
        cleanupWebRTCRef.current();
        setActiveCall(null);
        setCallState('IDLE');
      } else if (signal.type === 'CALL_REJECT' || signal.type === 'CALL_BUSY') {
        console.log(`[CALL:${callId}] Call ended by remote:`, signal.type);
        ringtonePlayer.stop();
        ringtonePlayer.playEndBeep();
        stopCallDuration();
        iAcceptedCallRef.current = false;
        isCallerRef.current = false;
        cleanupWebRTCRef.current();
        setActiveCall(null);
        setCallState('IDLE');
      } else if (signal.type === 'CALL_HANGUP') {
        console.log(`[CALL:${callId}] Remote peer hung up`);
        ringtonePlayer.stop();
        ringtonePlayer.playEndBeep();
        stopCallDuration();
        iAcceptedCallRef.current = false;
        isCallerRef.current = false;
        cleanupWebRTCRef.current();
        setActiveCall(null);
        setCallState('IDLE');
      }
    });

    return () => {
      unsubscribe();
      ringtonePlayer.stop();
      stopCallDuration();
    };
  }, [user?.id, stopCallDuration, startCallDuration]);

  return (
    <CallContext.Provider
      value={{
        callState,
        activeCall,
        localStream,
        remoteStream,
        screenStream,
        isMuted,
        isCameraOff,
        isScreenSharing,
        audioLevel,
        durationSeconds,
        initiateCall,
        acceptCall,
        rejectCall,
        endCall,
        toggleMute,
        toggleCamera,
        toggleScreenShare,
      }}
    >
      {children}
    </CallContext.Provider>
  );
};

export const useCall = () => {
  const context = useContext(CallContext);
  if (!context) {
    throw new Error('useCall must be used within a CallProvider');
  }
  return context;
};
